import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';

import { getCheckTypeIcon } from 'helpers/iconsHelper';
import { useLocale } from 'hooks/locale';
import { useSelector } from 'react-redux';

import { DatePicker, InputGroup, ToggleSwitchInput } from 'components/FormComponents';

import { selectCompany } from 'api/company-slice';

export const RenewalsSelector = forwardRef(
  ({ renewalConfigs, companyChecks, onSubmit = () => {} }, ref) => {
    const [selectedRenewalConfigs, setSelectedRenewalConfigs] = useState([]);

    const handleRenewalConfigChange = renewalConfig => {
      const renewalIndex = selectedRenewalConfigs.findIndex(
        config => config.id === renewalConfig.id,
      );
      if (renewalIndex < 0 && renewalConfig.enabled) {
        setSelectedRenewalConfigs(prev => [...prev, renewalConfig]);
      } else if (renewalIndex >= 0 && !renewalConfig.enabled) {
        setSelectedRenewalConfigs(prev => prev.filter(config => config.id !== renewalConfig.id));
      } else if (renewalIndex >= 0 && renewalConfig.enabled) {
        setSelectedRenewalConfigs(prev =>
          prev.map((config, index) => (index === renewalIndex ? renewalConfig : config)),
        );
      }
    };

    useImperativeHandle(ref, () => ({
      submit: () => onSubmit(selectedRenewalConfigs),
      getValues: () => selectedRenewalConfigs,
    }));

    return (
      <div>
        {renewalConfigs?.map(renewalConfig => (
          <SelectorDetails
            key={renewalConfig.id}
            renewalConfig={renewalConfig}
            companyChecks={companyChecks}
            onChange={handleRenewalConfigChange}
          />
        ))}
      </div>
    );
  },
);

const SelectorDetails = ({ renewalConfig, companyChecks, onChange = () => {} }) => {
  const company = useSelector(selectCompany);
  const { dateHyphenFormat } = useLocale(company?.locale);

  const [enabled, setEnabled] = useState(false);
  const [renewalDate, setRenewalDate] = useState(null);

  useEffect(() => {
    onChange({ id: renewalConfig.id, enabled, renewal_date: renewalDate });
  }, [enabled, renewalDate]);

  const checkType = useMemo(() => {
    return companyChecks.find(companyCheck => companyCheck.type === renewalConfig.type)?.check_type;
  }, [renewalConfig, companyChecks]);

  const title = useMemo(() => {
    if (!checkType) {
      return '';
    }

    return renewalConfig.name !== checkType.title
      ? `${renewalConfig.name} (${checkType.title})`
      : renewalConfig.name;
  }, [renewalConfig, checkType]);

  const screeningFrequency = useMemo(() => {
    if (renewalConfig.renewal_mode === 'use_expiration') {
      return `Re-screen ${renewalConfig.days_before_expiration} days before expiration`;
    }

    return `Re-screen every ${renewalConfig.frequency} days`;
  });

  return (
    <div>
      <ToggleSwitchInput
        text={title}
        subtext={screeningFrequency}
        logo={checkType?.logo}
        icon={getCheckTypeIcon(checkType?.slug)}
        dataTestId={`toggle-switch-${checkType?.slug}`}
        value={enabled}
        onChange={e => setEnabled(e.target.checked)}>
        {enabled && (
          <div>
            <InputGroup title="First Renewal Date">
              <DatePicker
                format={dateHyphenFormat}
                outputFormat="YYYY-MM-DD"
                value={renewalDate}
                minDate={new Date()}
                onChange={setRenewalDate}
                useDefaultToday
              />
            </InputGroup>
          </div>
        )}
      </ToggleSwitchInput>
    </div>
  );
};
